import { useState, useRef, useEffect } from 'react';
import { FolderWithMeta } from '../../api';
import { getErrorMessage, highlightMatch } from '../../../../shared/src';
import { SelectionBar } from './SelectionBar';
import { createMultiDragGhost } from './createMultiDragGhost';

interface FolderListProps {
  folders: FolderWithMeta[];
  onOpen: (folderId: string) => void;
  onRename: (folderId: string, newName: string) => Promise<void>;
  onDelete: (folder: FolderWithMeta) => void;
  onFileDrop?: (folderId: string, fileId: string) => void;
  onFolderDrop?: (targetFolderId: string, movedFolderId: string) => void;
  onMultiFileDrop?: (targetFolderId: string, fileIds: string[]) => void;
  onMultiFolderDrop?: (targetFolderId: string, folderIds: string[]) => void;
  // Inline folder creation props
  isCreatingNew?: boolean;
  onCreateNew?: (name: string) => Promise<void>;
  onCancelCreate?: () => void;
  // Multi-select props
  multiSelectedIds: Set<string>;
  hasMultiSelection: boolean;
  onMultiSelectToggle: (id: string, event: React.MouseEvent) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onDeleteSelected: () => void;
  deletingSelected?: boolean;
  /** Active search term, for highlighting matched folder names. */
  searchTerm?: string;
  /** Navigate to a folder by id (project-wide search: jump to where a match lives). */
  onNavigateToFolder?: (folderId: string | null) => void;
}

const FolderPathIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

export function FolderList({
  folders,
  searchTerm,
  onNavigateToFolder,
  onOpen,
  onRename,
  onDelete,
  onFileDrop,
  onFolderDrop,
  onMultiFileDrop,
  onMultiFolderDrop,
  isCreatingNew,
  onCreateNew,
  onCancelCreate,
  multiSelectedIds,
  hasMultiSelection,
  onMultiSelectToggle,
  onSelectAll,
  onClearSelection,
  onDeleteSelected,
  deletingSelected,
}: FolderListProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editError, setEditError] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Track which folder IDs are currently being dragged (ref to avoid re-renders)
  const draggingFolderIdsRef = useRef<Set<string>>(new Set());

  // New folder creation state
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderError, setNewFolderError] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const createInputRef = useRef<HTMLInputElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingId]);

  // Focus input when creating new folder
  useEffect(() => {
    if (isCreatingNew && createInputRef.current) {
      createInputRef.current.focus();
      setNewFolderName('');
      setNewFolderError('');
    }
  }, [isCreatingNew]);

  const startEditing = (folder: FolderWithMeta) => {
    setEditingId(folder.id);
    setEditValue(folder.name);
    setEditError('');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditValue('');
    setEditError('');
  };

  // New folder creation handlers
  const handleCreateNewFolder = async () => {
    const trimmedName = newFolderName.trim();

    if (!trimmedName) {
      onCancelCreate?.();
      return;
    }

    if (trimmedName.length > 255) {
      setNewFolderError('Name is too long');
      return;
    }

    if (/[<>:"/\\|?*]/.test(trimmedName)) {
      setNewFolderError('Invalid characters');
      return;
    }

    try {
      setCreatingFolder(true);
      await onCreateNew?.(trimmedName);
      setNewFolderName('');
      setNewFolderError('');
    } catch (err: unknown) {
      setNewFolderError(getErrorMessage(err));
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleCreateKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateNewFolder();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancelCreate?.();
    }
  };

  const handleCreateBlur = () => {
    if (!creatingFolder) {
      if (!newFolderName.trim()) {
        onCancelCreate?.();
      } else {
        handleCreateNewFolder();
      }
    }
  };

  const saveEdit = async (folder: FolderWithMeta) => {
    const trimmedName = editValue.trim();

    if (!trimmedName) {
      setEditError('Name is required');
      return;
    }

    if (trimmedName.length > 255) {
      setEditError('Name is too long');
      return;
    }

    if (/[<>:"/\\|?*]/.test(trimmedName)) {
      setEditError('Invalid characters');
      return;
    }

    if (trimmedName === folder.name) {
      cancelEditing();
      return;
    }

    try {
      setSaving(true);
      await onRename(folder.id, trimmedName);
      cancelEditing();
    } catch (err: unknown) {
      setEditError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, folder: FolderWithMeta) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit(folder);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditing();
    }
  };

  const handleFolderDragStart = (e: React.DragEvent, folder: FolderWithMeta) => {
    e.dataTransfer.effectAllowed = 'move';

    const isMultiDrag = multiSelectedIds.has(folder.id) && multiSelectedIds.size > 1;

    // Track which folders are being dragged so we can exclude them as drop targets
    if (isMultiDrag) {
      draggingFolderIdsRef.current = new Set(multiSelectedIds);
    } else {
      draggingFolderIdsRef.current = new Set([folder.id]);
    }

    // If this folder is part of a multi-selection, drag all selected folders
    if (isMultiDrag) {
      const allIds = Array.from(multiSelectedIds).join(',');
      e.dataTransfer.setData('application/x-folder-ids', allIds);
      e.dataTransfer.setData('application/x-folder-id', folder.id);
    } else {
      e.dataTransfer.setData('application/x-folder-id', folder.id);
    }

    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const cursorOffset = { x: e.clientX - rect.left, y: e.clientY - rect.top };

    if (isMultiDrag) {
      // Finder-style stacked ghost with count badge
      const ghost = createMultiDragGhost(target, multiSelectedIds.size, cursorOffset);
      e.dataTransfer.setDragImage(ghost.element, ghost.offsetX, ghost.offsetY);
      setTimeout(() => document.body.removeChild(ghost.element), 0);
    } else {
      // Single item: simple scaled clone
      const scale = 0.85;
      const clone = target.cloneNode(true) as HTMLElement;
      clone.style.cssText = `
        position: absolute;
        top: -9999px;
        left: -9999px;
        width: ${rect.width * scale}px;
        height: ${rect.height * scale}px;
        opacity: 0.7;
        pointer-events: none;
        background: var(--bg-secondary, #f9fafb);
        border-radius: 8px;
        overflow: hidden;
      `;
      document.body.appendChild(clone);
      clone.offsetHeight;

      const offsetX = cursorOffset.x * scale;
      const offsetY = cursorOffset.y * scale;
      e.dataTransfer.setDragImage(clone, offsetX, offsetY);
      setTimeout(() => document.body.removeChild(clone), 0);
    }
  };

  const handleFolderDragEnd = () => {
    draggingFolderIdsRef.current = new Set();
    setDragOverId(null);
  };

  const handleDragOver = (e: React.DragEvent, folderId: string) => {
    e.preventDefault();
    e.stopPropagation();

    // Don't allow dropping on a folder that is itself being dragged
    if (draggingFolderIdsRef.current.has(folderId)) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }

    const hasFile = e.dataTransfer.types.includes('application/x-file-id');
    const hasFolder = e.dataTransfer.types.includes('application/x-folder-id');
    const hasMultiFiles = e.dataTransfer.types.includes('application/x-file-ids');
    const hasMultiFolders = e.dataTransfer.types.includes('application/x-folder-ids');
    if (hasFile || hasFolder || hasMultiFiles || hasMultiFolders) {
      e.dataTransfer.dropEffect = 'move';
      setDragOverId(folderId);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverId(null);
  };

  const handleDrop = (e: React.DragEvent, folderId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverId(null);

    // Don't allow dropping on a folder that is itself being dragged
    if (draggingFolderIdsRef.current.has(folderId)) {
      return;
    }

    // Multi-file drop
    const multiFileIds = e.dataTransfer.getData('application/x-file-ids');
    if (multiFileIds && onMultiFileDrop) {
      const ids = multiFileIds.split(',');
      onMultiFileDrop(folderId, ids);
      return;
    }

    // Single file drop
    const fileId = e.dataTransfer.getData('application/x-file-id');
    if (fileId && onFileDrop) {
      onFileDrop(folderId, fileId);
      return;
    }

    // Multi-folder drop
    const multiFolderIds = e.dataTransfer.getData('application/x-folder-ids');
    if (multiFolderIds && onMultiFolderDrop) {
      const ids = multiFolderIds.split(',').filter((id) => id !== folderId);
      if (ids.length > 0) {
        onMultiFolderDrop(folderId, ids);
      }
      return;
    }

    // Single folder drop
    const movedFolderId = e.dataTransfer.getData('application/x-folder-id');
    if (movedFolderId && movedFolderId !== folderId && onFolderDrop) {
      onFolderDrop(folderId, movedFolderId);
    }
  };

  return (
    <div className="folder-section">
      <h4 className="section-title">Folders</h4>
      {hasMultiSelection && (
        <SelectionBar
          count={multiSelectedIds.size}
          totalCount={folders.length}
          itemLabel="folder"
          onSelectAll={onSelectAll}
          onClearSelection={onClearSelection}
          onDeleteSelected={onDeleteSelected}
          deleting={deletingSelected}
        />
      )}
      <div className="folder-grid">
        {/* Phantom folder for inline creation */}
        {isCreatingNew && (
          <div className="folder-item folder-item-editing folder-item-new">
            <div className="folder-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V8a2 2 0 00-2-2h-8l-2-2z" />
              </svg>
            </div>
            <div className="folder-name-edit">
              <input
                ref={createInputRef}
                type="text"
                value={newFolderName}
                onChange={(e) => {
                  setNewFolderName(e.target.value);
                  setNewFolderError('');
                }}
                onBlur={handleCreateBlur}
                onKeyDown={handleCreateKeyDown}
                disabled={creatingFolder}
                placeholder="New folder"
                className={newFolderError ? 'input-error' : ''}
              />
              {newFolderError && <span className="edit-error">{newFolderError}</span>}
            </div>
          </div>
        )}

        {folders.map((folder) => {
          const isEditing = editingId === folder.id;
          const isDragOver = dragOverId === folder.id;
          const isMultiSelected = multiSelectedIds.has(folder.id);

          return (
            <div
              key={folder.id}
              className={`folder-item ${isEditing ? 'folder-item-editing' : ''} ${isDragOver ? 'folder-item-drag-over' : ''} ${isMultiSelected ? 'folder-item-multi-selected' : ''}`}
              draggable={!isEditing}
              onDragStart={(e) => handleFolderDragStart(e, folder)}
              onDragEnd={handleFolderDragEnd}
              onClick={(e) => {
                if (isEditing) return;
                if (e.metaKey || e.ctrlKey || e.shiftKey || hasMultiSelection) {
                  onMultiSelectToggle(folder.id, e);
                }
              }}
              onDoubleClick={() => !isEditing && onOpen(folder.id)}
              onDragOver={(e) => handleDragOver(e, folder.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, folder.id)}
            >
              {/* Multi-select checkbox */}
              <div
                className={`item-checkbox ${hasMultiSelection ? 'checkbox-visible' : 'checkbox-hover-only'} ${isMultiSelected ? 'checkbox-checked' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onMultiSelectToggle(folder.id, e);
                }}
              >
                {isMultiSelected && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>

              <div className="folder-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V8a2 2 0 00-2-2h-8l-2-2z" />
                </svg>
              </div>

              {isEditing ? (
                <div className="folder-name-edit">
                  <input
                    ref={inputRef}
                    type="text"
                    value={editValue}
                    onChange={(e) => {
                      setEditValue(e.target.value);
                      setEditError('');
                    }}
                    onBlur={() => saveEdit(folder)}
                    onKeyDown={(e) => handleKeyDown(e, folder)}
                    disabled={saving}
                    className={editError ? 'input-error' : ''}
                  />
                  {editError && <span className="edit-error">{editError}</span>}
                </div>
              ) : (
                <div className="folder-name" title={folder.name}>
                  {highlightMatch(folder.name, searchTerm)}
                </div>
              )}

              {/* Project-wide search: show (and link to) where this folder lives. */}
              {folder.folder_path !== undefined && (
                <button
                  type="button"
                  className="folder-folder-path"
                  title={`Go to ${folder.folder_path || 'project root'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onNavigateToFolder?.(folder.parent_id);
                  }}
                >
                  <FolderPathIcon />
                  <span className="folder-folder-path-text">{folder.folder_path || 'Project root'}</span>
                </button>
              )}

              <div className="folder-meta">
                {folder.subfolder_count > 0 && (
                  <span className="folder-count">{folder.subfolder_count} folder(s)</span>
                )}
                {folder.file_count > 0 && <span className="file-count">{folder.file_count} file(s)</span>}
                {Number(folder.subfolder_count) === 0 && Number(folder.file_count) === 0 && (
                  <span className="folder-empty">Empty</span>
                )}
              </div>

              {!isEditing && (
                <div className="folder-actions">
                  <button
                    className="action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      startEditing(folder);
                    }}
                    title="Rename"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                  <button
                    className="action-btn action-btn-danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(folder);
                    }}
                    title="Delete"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
